// @flow

import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { actions as toastrActions, toastr } from 'react-redux-toastr';
import useDeleteGrossRentChangeMutation from './hooks.useDeleteGrossRentChangeMutation';
import useIntl from '../../../hooks/useIntl';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import { extractErrorMessage } from '../../../utils/errors';
import generalMessages from '../../App/messages';
import messages from './messages';

const DELETE_GROSS_RENT_CHANGE_IN_PROGRESS_TOAST_ID =
  'delete-gross-rent-change-in-progress';

/**
 * Owns the delete-confirmation-modal state and the delete mutation's side effects (the purple
 * in-progress toast dispatched on mutate, dismissed and replaced by a success/error toast plus
 * cache invalidation on settle) for one Gross Rent Change row, so
 * GrossRentChangeDrawerActionsMenu only has to wire up menu/modal presentation.
 */
const useDeleteGrossRentChangeAction = ({
  effectiveDate,
}: {
  effectiveDate: string,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const deleteMutation = useDeleteGrossRentChangeMutation({
    organizationId,
    propertyId,
    options: {
      onMutate: () => {
        setIsDeleteModalOpen(false);
        dispatch(
          toastrActions.add({
            id: DELETE_GROSS_RENT_CHANGE_IN_PROGRESS_TOAST_ID,
            type: 'info',
            title: intl.formatMessage(
              messages.deleteGrossRentChangeInProgressTitle,
            ),
            message: intl.formatMessage(
              messages.deleteGrossRentChangeInProgressMessage,
            ),
            options: {
              timeOut: 0,
              className: 'rrt-purple',
              removeOnHover: false,
              showCloseButton: true,
            },
          }),
        );
      },
      onSuccess: () => {
        dispatch(
          toastrActions.remove(DELETE_GROSS_RENT_CHANGE_IN_PROGRESS_TOAST_ID),
        );
        queryClient.invalidateQueries([
          'grossRentChanges',
          organizationId,
          propertyId,
        ]);
        queryClient.invalidateQueries([
          'grossRentCertifications',
          organizationId,
          propertyId,
        ]);
        // Deleting the change removes its floor plan pricing rows, which are
        // what the wizard's "Current" gross rent / utility allowance columns
        // are derived from. Without this the wizard reopens showing the
        // just-deleted amounts until the 60s staleTime lapses.
        queryClient.invalidateQueries([
          'hudFloorPlansWithPricing',
          organizationId,
          propertyId,
        ]);
        toastr.success(
          intl.formatMessage(generalMessages.success),
          intl.formatMessage(messages.deleteGrossRentChangeSuccess),
        );
      },
      onError: (error) => {
        dispatch(
          toastrActions.remove(DELETE_GROSS_RENT_CHANGE_IN_PROGRESS_TOAST_ID),
        );
        toastr.error(
          intl.formatMessage(generalMessages.error),
          intl.formatMessage(messages.errorDeletingGrossRentChange, {
            errorMessage: extractErrorMessage(error),
          }),
        );
      },
    },
  });

  const isSubmitting = deleteMutation.isLoading;

  const openDeleteModal = useCallback(() => setIsDeleteModalOpen(true), []);
  const closeDeleteModal = useCallback(() => setIsDeleteModalOpen(false), []);
  const confirmDelete = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    deleteMutation.mutate({ effectiveDate });
  }, [deleteMutation, effectiveDate, isSubmitting]);

  return {
    isDeleteModalOpen,
    isSubmitting,
    openDeleteModal,
    closeDeleteModal,
    confirmDelete,
  };
};

export default useDeleteGrossRentChangeAction;
